import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConsentProvider } from "@/context/ConsentContext";
import CookieBanner from "@/components/CookieBanner";
import NewsletterPopup from "@/components/NewsletterPopup";

// Landing bleibt eager (schnellster erster Seitenaufbau + SEO), alles Weitere lazy geladen,
// damit das initiale JS-Bundle klein bleibt und die Seite schneller lädt.
import Landing from "@/pages/Landing";
const Zielgruppe = lazy(() => import("@/pages/Zielgruppe"));
const SoFunktioniert = lazy(() => import("@/pages/SoFunktioniert"));
const LoesungKommunen = lazy(() => import("@/pages/LoesungKommunen"));
const LoesungUnternehmen = lazy(() => import("@/pages/LoesungUnternehmen"));
const LoesungAgenturen = lazy(() => import("@/pages/LoesungAgenturen"));
const LoesungPersonen = lazy(() => import("@/pages/LoesungPersonen"));
const DemoIntro = lazy(() => import("@/pages/DemoIntro"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Impressum = lazy(() => import("@/pages/Impressum"));
const Datenschutz = lazy(() => import("@/pages/Datenschutz"));
const CookieRichtlinie = lazy(() => import("@/pages/CookieRichtlinie"));
const Agb = lazy(() => import("@/pages/Agb"));
const Widerruf = lazy(() => import("@/pages/Widerruf"));
const Kuendigung = lazy(() => import("@/pages/Kuendigung"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const DemoBootstrap = lazy(() => import("@/pages/DemoBootstrap"));
const NewsletterConfirm = lazy(() => import("@/pages/NewsletterConfirm"));
const NewsletterUnsubscribe = lazy(() => import("@/pages/NewsletterUnsubscribe"));
const AdminNewsletter = lazy(() => import("@/pages/dashboard/AdminNewsletter"));
const Preise = lazy(() => import("@/pages/Preise"));
const Quellen = lazy(() => import("@/pages/Quellen"));
const VerifyPending = lazy(() => import("@/pages/VerifyPending"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const RecipientVerify = lazy(() => import("@/pages/RecipientVerify"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const ReputationManagement = lazy(() => import("@/pages/ReputationManagement"));
const MediaMonitoring = lazy(() => import("@/pages/MediaMonitoring"));
const CrisisMonitoring = lazy(() => import("@/pages/CrisisMonitoring"));
const MarkenMonitoring = lazy(() => import("@/pages/MarkenMonitoring"));
const NewsAlerts = lazy(() => import("@/pages/NewsAlerts"));
const CompetitorMonitoring = lazy(() => import("@/pages/CompetitorMonitoring"));
const WasIstNewzer = lazy(() => import("@/pages/WasIstNewzer"));
const GoogleAlertsAlternative = lazy(() => import("@/pages/GoogleAlertsAlternative"));
const Glossar = lazy(() => import("@/pages/Glossar"));
const Transparenz = lazy(() => import("@/pages/Transparenz"));
const Fruehwarnsystem = lazy(() => import("@/pages/Fruehwarnsystem"));
const RssMonitoring = lazy(() => import("@/pages/RssMonitoring"));
const RatgeberFruehwarnsystem = lazy(() => import("@/pages/RatgeberFruehwarnsystem"));
const RatgeberGoogleAlerts = lazy(() => import("@/pages/RatgeberGoogleAlerts"));
const RatgeberMedienbeobachtung = lazy(() => import("@/pages/RatgeberMedienbeobachtung"));
const RatgeberReputationsmonitoring = lazy(() => import("@/pages/RatgeberReputationsmonitoring"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const DashboardLayout = lazy(() => import("@/pages/dashboard/DashboardLayout"));
const Overview = lazy(() => import("@/pages/dashboard/Overview"));
const Alerts = lazy(() => import("@/pages/dashboard/Alerts"));
const AlertRecipients = lazy(() => import("@/pages/dashboard/AlertRecipients"));
const AdminEmailLog = lazy(() => import("@/pages/dashboard/AdminEmailLog"));
const Orders = lazy(() => import("@/pages/dashboard/Orders"));
const Briefings = lazy(() => import("@/pages/dashboard/Briefings"));
const Mentions = lazy(() => import("@/pages/dashboard/Mentions"));
const Searches = lazy(() => import("@/pages/dashboard/Searches"));
const NewSearch = lazy(() => import("@/pages/dashboard/NewSearch"));
const Channels = lazy(() => import("@/pages/dashboard/Channels"));
const PersonProfile = lazy(() => import("@/pages/dashboard/PersonProfile"));
const Images = lazy(() => import("@/pages/dashboard/Images"));
const SmsWebhooks = lazy(() => import("@/pages/dashboard/SmsWebhooks"));
const Team = lazy(() => import("@/pages/dashboard/Team"));
const Settings = lazy(() => import("@/pages/dashboard/Settings"));
const SystemMonitor = lazy(() => import("@/pages/dashboard/SystemMonitor"));
const AssessmentShadow = lazy(() => import("@/pages/dashboard/AssessmentShadow"));
const Zustellverlauf = lazy(() => import("@/pages/dashboard/Zustellverlauf"));
const Zustellung = lazy(() => import("@/pages/dashboard/Zustellung"));
const QuellenKanaele = lazy(() => import("@/pages/dashboard/QuellenKanaele"));
const RegistryAdmin = lazy(() => import("@/pages/dashboard/RegistryAdmin"));
const Tasks = lazy(() => import("@/pages/dashboard/Tasks"));
const Events = lazy(() => import("@/pages/dashboard/Events"));
const Admin = lazy(() => import("@/pages/dashboard/Admin"));
const Comparisons = lazy(() => import("@/pages/dashboard/Comparisons"));
const Campaigns = lazy(() => import("@/pages/dashboard/Campaigns"));
const LiveDashboardConfig = lazy(() => import("@/pages/dashboard/LiveDashboardConfig"));
const Wallboard = lazy(() => import("@/pages/Wallboard"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-fc-ivory">
      <div className="h-10 w-10 rounded-full border-2 border-fc-blue border-t-transparent animate-spin" />
    </div>
  );
}

function HashScroll() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    let tries = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (tries < 10) {
        tries += 1;
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
  }, [pathname, hash]);
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-fc-ivory">
        <div className="h-10 w-10 rounded-full border-2 border-fc-blue border-t-transparent animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/anmelden" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <ConsentProvider>
        <BrowserRouter>
          <HashScroll />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/so-funktioniert-newzer" element={<SoFunktioniert />} />
            <Route path="/loesungen/kommunen" element={<LoesungKommunen />} />
            <Route path="/loesungen/unternehmen" element={<LoesungUnternehmen />} />
            <Route path="/loesungen/agenturen" element={<LoesungAgenturen />} />
            <Route path="/loesungen/personen" element={<LoesungPersonen />} />
            <Route path="/loesungen/:slug" element={<Zielgruppe />} />
            <Route path="/anmelden" element={<Login />} />
            <Route path="/registrieren" element={<Register />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/cookie-richtlinie" element={<CookieRichtlinie />} />
            <Route path="/agb" element={<Agb />} />
            <Route path="/widerruf" element={<Widerruf />} />
            <Route path="/vertrag-kuendigen" element={<Kuendigung />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/demo" element={<DemoIntro />} />
            <Route path="/demo/start" element={<DemoBootstrap />} />
            <Route path="/live/:token" element={<Wallboard />} />
            <Route path="/preise" element={<Preise />} />
            <Route path="/quellen" element={<Quellen />} />
            <Route path="/registrierung-bestaetigen" element={<VerifyPending />} />
            <Route path="/verifizieren" element={<VerifyEmail />} />
            <Route path="/empfaenger-bestaetigen" element={<RecipientVerify />} />
            <Route path="/team-einladung" element={<AcceptInvite />} />
            <Route path="/newsletter/bestaetigen" element={<NewsletterConfirm />} />
            <Route path="/newsletter/abmelden" element={<NewsletterUnsubscribe />} />
            <Route path="/reputationsmanagement" element={<ReputationManagement />} />
            <Route path="/medienmonitoring" element={<MediaMonitoring />} />
            <Route path="/krisenmonitoring" element={<CrisisMonitoring />} />
            <Route path="/markenmonitoring" element={<MarkenMonitoring />} />
            <Route path="/news-alerts" element={<NewsAlerts />} />
            <Route path="/wettbewerbsbeobachtung" element={<CompetitorMonitoring />} />
            <Route path="/was-ist-newzer" element={<WasIstNewzer />} />
            <Route path="/google-alerts-alternative" element={<GoogleAlertsAlternative />} />
            <Route path="/glossar" element={<Glossar />} />
            <Route path="/transparenz" element={<Transparenz />} />
            <Route path="/fruehwarnsystem" element={<Fruehwarnsystem />} />
            <Route path="/rss-monitoring" element={<RssMonitoring />} />
            <Route path="/ratgeber/fruehwarnsystem" element={<RatgeberFruehwarnsystem />} />
            <Route path="/ratgeber/google-alerts-alternative" element={<RatgeberGoogleAlerts />} />
            <Route path="/ratgeber/medienbeobachtung" element={<RatgeberMedienbeobachtung />} />
            <Route path="/ratgeber/reputationsmonitoring" element={<RatgeberReputationsmonitoring />} />
            {/* SEO-Keyword-URLs: saubere Weiterleitung auf die kanonische Bestandsseite (kein Duplicate Content). */}
            <Route path="/medienbeobachtung" element={<Navigate to="/medienmonitoring" replace />} />
            <Route path="/reputationsmonitoring" element={<Navigate to="/reputationsmanagement" replace />} />
            <Route path="/krisenfrueherkennung" element={<Navigate to="/krisenmonitoring" replace />} />
            <Route path="/personen-monitoring" element={<Navigate to="/loesungen/personen" replace />} />
            <Route path="/kommunen" element={<Navigate to="/loesungen/kommunen" replace />} />
            <Route path="/unternehmen-beobachten" element={<Navigate to="/loesungen/unternehmen" replace />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <DashboardLayout />
                </Protected>
              }
            >
              <Route index element={<Overview />} />
              <Route path="alarme" element={<Alerts />} />
              <Route path="alarme/empfaenger" element={<AlertRecipients />} />
              <Route path="admin/zustellung" element={<AdminEmailLog />} />
              <Route path="briefings" element={<Briefings />} />
              <Route path="aufgaben" element={<Tasks />} />
              <Route path="ereignisse" element={<Events />} />
              <Route path="vergleiche" element={<Comparisons />} />
              <Route path="auswertungen" element={<Campaigns />} />
              <Route path="kampagnen" element={<Navigate to="/app/auswertungen" replace />} />
              <Route path="krisenmodus" element={<Navigate to="/app/briefings" replace />} />
              <Route path="krisenlage" element={<Navigate to="/app/briefings" replace />} />
              <Route path="admin" element={<Admin />} />
              <Route path="admin/newsletter" element={<AdminNewsletter />} />
              <Route path="rechnungen" element={<Orders />} />
              <Route path="erwaehnungen" element={<Mentions />} />
              <Route path="person/:sid" element={<PersonProfile />} />
              <Route path="suchen" element={<Searches />} />
              <Route path="suchen/neu" element={<NewSearch />} />
              <Route path="kanaele" element={<Channels />} />
              <Route path="bilder" element={<Images />} />
              <Route path="sms-webhooks" element={<SmsWebhooks />} />
              <Route path="reports" element={<Navigate to="/app/auswertungen" replace />} />
              <Route path="team" element={<Team />} />
              <Route path="einstellungen" element={<Settings />} />
              <Route path="systemmonitor" element={<SystemMonitor />} />
              <Route path="ki-bewertung" element={<AssessmentShadow />} />
              <Route path="zustellverlauf" element={<Zustellverlauf />} />
              <Route path="zustellung" element={<Zustellung />} />
              <Route path="quellen-kanaele" element={<QuellenKanaele />} />
              <Route path="registry" element={<RegistryAdmin />} />
              <Route path="live-dashboard" element={<LiveDashboardConfig />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <CookieBanner />
          <NewsletterPopup />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </ConsentProvider>
    </AuthProvider>
  );
}

export default App;
